import type { ICreateIssueCmd } from "@/data/commands/support/CreateIssueCmd";
import { Factory } from "@/data/dao/Factory";
import { SupportDao } from "@/data/dao/service/SupportDao";
import { Validation } from "@/data/util/Validation";

import { logError } from "@/shared/logging/errors";
import { FloatLabel } from "@/shared/ui/FloatLabel";
import { Ripple } from "@/shared/ui/Ripple";

import { TastierClient } from "@/app/TastierClient";
import { BaseController } from "@/app/controller/BaseController";
import { EmailInput } from "@/app/elements/EmailInput";
import { PhoneNumberInput } from "@/app/elements/PhoneNumberInput";
import { trans } from "@/app/localization/Trans";
import { Popup } from "@/app/ui/Popup";

export class ReportIssueController extends BaseController {
    public onGoBack?: { () };

    private $issueForm: JQuery;
    private $saveBtn: JQuery;

    private $firstNameInput: JQuery;
    private $lastNameInput: JQuery;
    private $descriptionInput: JQuery;
    private emailInput: EmailInput;
    private phoneInput: PhoneNumberInput;

    constructor() {
        super("reportissue");

        const hdr = $('<div class="head"><div class="title">' + trans.help.reportProblemBtn + "</div></div>").appendTo(
            this.$view
        );
        Ripple.bind($('<a class="action left" href="#"><i class="fal fa-arrow-left"></i></a>'))
            .prependTo(hdr)
            .click((e) => {
                e.preventDefault();
                if (this.onGoBack !== undefined) this.onGoBack();
            });

        const scrollable = $('<div class="scrollable" />').appendTo(this.$view);
        this.$issueForm = $('<form class="form" />').appendTo(scrollable);
        this.$issueForm.submit((e) => {
            e.preventDefault();
            this.sendIssue();

            return false;
        });

        let row = $('<div class="formrow splitted" />').appendTo(this.$issueForm);
        let col = $('<div class="col" />').appendTo(row);
        col.append(
            '<div class="formcontrol"><label>' +
                trans.signup.firstName +
                '</label><input type="text" name="firstname" autocomplete="off" value="" /></div>'
        );
        this.$firstNameInput = col.find('input[name="firstname"]');

        col = $('<div class="col" />').appendTo(row);
        col.append(
            '<div class="formcontrol"><label>' +
                trans.signup.lastName +
                '</label><input type="text" name="lastname" autocomplete="off" value="" /></div>'
        );
        this.$lastNameInput = col.find('input[name="lastname"]');

        row = $('<div class="formrow" />').appendTo(this.$issueForm);
        this.emailInput = new EmailInput(false).appendTo(row);

        row = $('<div class="formrow" />').appendTo(this.$issueForm);
        this.phoneInput = new PhoneNumberInput(false).appendTo(row);

        const descRow = $('<div class="formrow" />').appendTo(this.$issueForm);
        descRow.append(
            '<div class="formcontrol edited textarea"><label>' + trans.help.reportProblem + "</label></div>"
        );
        this.$descriptionInput = $('<textarea name="descr" autocomplete="off" value=""></textarea>').appendTo(
            descRow.find("> .formcontrol")
        );

        this.$saveBtn = Ripple.bind(
            $('<button type="submit">' + trans.help.sendBtn + "</button>")
                .appendTo(this.$issueForm)
                .click((e) => {
                    e.preventDefault();
                    if (this.$saveBtn.hasClass("disabled") || this.$saveBtn.hasClass("loading")) {
                        e.stopPropagation();
                        return;
                    }

                    this.$issueForm.submit();
                })
        );

        FloatLabel.bind(this.$issueForm);
    }

    protected onBecomeVisible() {
        const profile = TastierClient.identityContext.getProfile()!;
        this.$firstNameInput.val(profile.firstName === undefined ? "" : profile.firstName);
        this.$lastNameInput.val(profile.lastName === undefined ? "" : profile.lastName);
        this.emailInput.setValue(profile.email === undefined ? "" : profile.email);
        this.phoneInput.setValue(profile.phoneNumber === undefined ? "" : profile.phoneNumber);

        this.$descriptionInput.val("");
        this.$saveBtn.removeClass("loading");
        this.$issueForm.find(".has-error").removeClass("has-error");

        FloatLabel.check(this.$issueForm);
    }

    protected onBecomeInvisible() {}

    private async sendIssue() {
        const cmd: ICreateIssueCmd = {
            text: this.$descriptionInput.val().length === 0 ? undefined : this.$descriptionInput.val(),
            email: this.emailInput.getValue(),
            firstName: this.$firstNameInput.val().length === 0 ? undefined : this.$firstNameInput.val(),
            lastName: this.$lastNameInput.val().length === 0 ? undefined : this.$lastNameInput.val(),
            phoneCountryCode: this.phoneInput.getCountryCode()!,
            phoneNumber: this.phoneInput.getNumber()!,
        };

        let hasError = false;
        this.$issueForm.find(".has-error").removeClass("has-error");

        if (cmd.firstName === undefined) {
            this.$firstNameInput.parent().addClass("has-error");
            hasError = true;
        }

        if (cmd.lastName === undefined) {
            this.$lastNameInput.parent().addClass("has-error");
            hasError = true;
        }

        if (cmd.text === undefined) {
            this.$descriptionInput.parent().addClass("has-error");
            hasError = true;
        }

        if (cmd.email === undefined || !Validation.isEmail(cmd.email)) {
            this.emailInput.setError(true);
            hasError = true;
        } else this.emailInput.setError(false);

        if (cmd.phoneNumber === undefined || cmd.phoneCountryCode === undefined || !this.phoneInput.isValid()) {
            this.phoneInput.setError(true);
            hasError = true;
        } else this.phoneInput.setError(false);

        if (hasError) return;

        this.$saveBtn.addClass("loading");
        try {
            await Factory.getInstance<SupportDao>(SupportDao).createIssue(cmd);
            Popup.show(trans.help.reportIssueSuccessful, () => {
                if (this.onGoBack !== undefined) {
                    this.onGoBack();
                }
            });
        } catch (ex) {
            logError(ex);
            this.$saveBtn.removeClass("loading");
            Popup.show(trans.help.failedReportIssue);
        }
    }
}
